/**
 * AttachmentDisplay Component - Frontend version
 * Renders attachments in message bubbles
 *
 * Features:
 * - Image/Video/Audio display with product support
 * - Product purchase flow
 * - Timer countdown for expiring products
 * - Unlock attempt tracking
 * - Responsive & beautiful UI
 */

import { useState, useEffect, useCallback } from 'react'
import PurchaseProductModal from '@/features/vault/PurchaseProductModal'
import { api } from '@/services/api'
import { CREATOR_NAME } from '@/config/brandConfig'

interface Timer {
  expires_at: string
  seconds_remaining: number
  has_timer: boolean
  is_expired: boolean
}

interface Attachment {
  id: string
  type: 'image' | 'video' | 'audio' | 'file' | 'gif'
  url: string
  filename: string
  size: number
  mime_type: string
  thumbnail_url?: string
  // Product-specific fields
  is_paid?: boolean
  is_purchased?: boolean
  product_id?: string
  price?: number
  title?: string
  description?: string
  preview_url?: string
  preview_s3_key?: string
  full_s3_key?: string
  media_type?: 'image' | 'video' | 'voice_note'
  duration_seconds?: number
  timer?: Timer
  // Freebie-specific fields
  is_freebie?: boolean
  freebie_id?: string
  freebie_unlocked?: boolean
  // Gift Card specific fields
  is_gift_card?: boolean
  gift_card_id?: string
  gift_card_code?: string
  gift_card_amount?: number
  gift_card_claimed?: boolean
  gift_card_sender_name?: string
  // Tip specific fields
  is_tip?: boolean
  amount?: number
  payment_intent_id?: string
  sent_at?: string
  // GIF specific fields
  klipy_id?: string
  is_external?: boolean
  blur_preview?: string
}

interface AttachmentDisplayProps {
  attachments: Attachment[]
  isSentByMe?: boolean
  recentlyUnlockedProducts?: Set<string>
}

export function AttachmentDisplay({ attachments, isSentByMe = false, recentlyUnlockedProducts }: AttachmentDisplayProps) {
  const [lightboxImage, setLightboxImage] = useState<string | null>(null)
  const [loadingStates, setLoadingStates] = useState<{ [key: string]: boolean }>({})
  const [errorStates, setErrorStates] = useState<{ [key: string]: boolean }>({})
  const [purchaseModalProduct, setPurchaseModalProduct] = useState<{ id: string; title: string; price: number; previewUrl?: string } | null>(null)
  const [timerStates, setTimerStates] = useState<{ [key: string]: number }>({})
  // Freebie unlock state
  const [unlockedFreebies, setUnlockedFreebies] = useState<{ [key: string]: { url: string } }>({})
  const [unlockingFreebies, setUnlockingFreebies] = useState<{ [key: string]: boolean }>({})
  // Gift card claim state
  const [claimedGiftCards, setClaimedGiftCards] = useState<{ [key: string]: boolean }>({})
  const [claimingGiftCards, setClaimingGiftCards] = useState<{ [key: string]: boolean }>({})

  // Initialize timers
  useEffect(() => {
    const initialTimers: { [key: string]: number } = {}
    attachments.forEach((attachment) => {
      if (attachment.timer?.has_timer && !attachment.timer.is_expired) {
        initialTimers[attachment.id] = attachment.timer.seconds_remaining
      }
    })

    setTimerStates(initialTimers)
  }, [attachments])

  // Update timers every second
  useEffect(() => {
    const interval = setInterval(() => {
      setTimerStates(prev => {
        const updated = { ...prev }
        Object.keys(updated).forEach(key => {
          if (updated[key] > 0) {
            updated[key] -= 1
          }
        })
        return updated
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  // Track unlock attempt
  const trackUnlockAttempt = useCallback(async (productId: string) => {
    try {
      const token = localStorage.getItem('auth_token')
      await fetch(`${api.baseURL}/api/products/${productId}/track-unlock`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
    } catch (error) {
      console.error('Failed to track unlock attempt:', error)
    }
  }, [])

  // Unlock freebie (one-click, no payment)
  const unlockFreebie = useCallback(async (freebieId: string) => {
    setUnlockingFreebies(prev => ({ ...prev, [freebieId]: true }))
    try {
      const token = localStorage.getItem('auth_token')

      const response = await fetch(`${api.baseURL}/api/freebie-unlocks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ freebie_id: freebieId })
      })

      if (!response.ok) {
        throw new Error('Failed to unlock freebie')
      }

      const data = await response.json()

      // Store the full URL
      setUnlockedFreebies(prev => ({
        ...prev,
        [freebieId]: { url: data.freebie.full_url }
      }))
    } catch (error) {
      console.error('Failed to unlock freebie:', error)
      alert('Failed to unlock. Please try again.')
    } finally {
      setUnlockingFreebies(prev => ({ ...prev, [freebieId]: false }))
    }
  }, [])

  // Claim gift card (one-click from chat)
  const claimGiftCard = useCallback(async (giftCardCode: string, _messageId?: string) => {
    setClaimingGiftCards(prev => ({ ...prev, [giftCardCode]: true }))
    try {
      const token = localStorage.getItem('auth_token')

      const response = await fetch(`${api.baseURL}/api/wallet/redeem`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: giftCardCode })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to claim gift card')
      }

      const data = await response.json()

      setClaimedGiftCards(prev => ({ ...prev, [giftCardCode]: true }))

      // Show success alert with amount
      alert(`${data.message}`)
    } catch (error) {
      console.error('Failed to claim gift card:', error)
      alert(error instanceof Error ? error.message : 'Failed to claim gift card. Please try again.')
    } finally {
      setClaimingGiftCards(prev => ({ ...prev, [giftCardCode]: false }))
    }
  }, [])

  // Format file size (handles undefined/null)
  const formatFileSize = (bytes?: number) => {
    if (!bytes || bytes <= 0) return '0 B'
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  // Format duration
  const formatDuration = (seconds: number | undefined) => {
    if (!seconds) return '0:00'
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = Math.floor(seconds % 60)
    if (h > 0) {
      return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    }
    return `${m}:${String(s).padStart(2, '0')}`
  }

  // Format timer countdown
  const formatTimer = (seconds: number) => {
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = seconds % 60
    if (h > 0) {
      return `${h}h ${m}m ${s}s`
    }
    if (m > 0) {
      return `${m}m ${s}s`
    }
    return `${s}s`
  }

  // Format audio title - show proper date-time instead of cryptic filename (always EST)
  const formatAudioTitle = (attachment: Attachment) => {
    // Voice-note label — strip any embedded wall-clock time (also covers old stored titles).
    // The message bubble already shows the time in the viewer's LOCAL time; the stored title's
    // fixed-timezone (EST) clock time would not match it. Duration is shown separately below.
    if (attachment.title && attachment.title.startsWith('Voice Message')) {
      return 'Voice Message'
    }
    // If there's already a nice title, use it
    if (attachment.title && !attachment.title.startsWith('voice_recording_')) {
      return attachment.title
    }

    // Try to extract timestamp from filename like "voice_recording_1736703456789.webm"
    const filename = attachment.filename || attachment.title || ''
    const match = filename.match(/voice_recording_(\d+)/)
    if (match) {
      const timestamp = parseInt(match[1])
      if (!isNaN(timestamp)) {
        const date = new Date(timestamp)
        return `Voice Message - ${date.toLocaleString('en-US', {
          timeZone: 'America/New_York',
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        })} EST`
      }
    }

    // Fallback
    return 'Voice Message'
  }

  // Handle image load
  const handleImageLoad = (id: string) => {
    setLoadingStates(prev => ({ ...prev, [id]: false }))
  }

  // Handle image error
  const handleImageError = (id: string) => {
    setLoadingStates(prev => ({ ...prev, [id]: false }))
    setErrorStates(prev => ({ ...prev, [id]: true }))
  }

  // Voice-note playback recovery. Re-mounts the <audio> once (via key change) so it re-attempts
  // loading the source. Fires ONLY when playback fails, so it can never break the happy path.
  // (Expired customer signed URLs are also re-signed on the next conversation load.)
  const [audioRetry, setAudioRetry] = useState<{ [key: string]: number }>({})
  const handleAudioError = (id: string) => {
    setAudioRetry(prev => {
      const current = prev[id] || 0
      if (current >= 1) return prev // retry once
      return { ...prev, [id]: current + 1 }
    })
  }

  // Render single attachment
  const renderAttachment = (attachment: Attachment) => {
    const key = attachment.id

    // Tip attachment - Apple Cash style
    if (attachment.is_tip) {
      // Format amount - no cents unless needed
      const tipAmount = attachment.amount || 0
      const formattedAmount = tipAmount % 1 === 0 ? `$${tipAmount}` : `$${tipAmount.toFixed(2)}`

      return (
        <div key={key} className="w-[200px]">
          <div
            className="rounded-2xl p-5 relative overflow-hidden"
            style={{
              background: '#1a1a1a'
            }}
          >
            {/* Tip label with icon - top right, horizontal */}
            <div className="absolute top-3 right-3 flex items-center gap-1.5 z-10">
              <svg className="w-3.5 h-3.5 text-white/50" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" />
              </svg>
              <span className="text-[11px] text-white/50 font-medium tracking-wide">Tip</span>
            </div>

            {/* Silver metallic amount */}
            <div className="pt-6 pb-3 text-center relative z-10">
              <span
                className="tip-silver-text"
                style={{
                  fontSize: '3rem',
                  fontWeight: 600,
                  letterSpacing: '-0.02em'
                }}
              >
                {formattedAmount}
              </span>
              <style>{`
                .tip-silver-text {
                  background: linear-gradient(180deg, #e8e8e8 0%, #c0c0c0 50%, #a8a8a8 100%);
                  -webkit-background-clip: text;
                  -webkit-text-fill-color: transparent;
                  background-clip: text;
                }
              `}</style>
            </div>
          </div>
        </div>
      )
    }

    // Gift Card attachment - check first before other types
    if (attachment.is_gift_card && attachment.gift_card_code) {
      const giftCardCode = attachment.gift_card_code
      const isClaimed = attachment.gift_card_claimed === true || claimedGiftCards[giftCardCode]
      const isClaiming = claimingGiftCards[giftCardCode]

      return (
        <div key={key} className="relative max-w-sm">
          {/* Gift card visual - night-sky theme */}
          <div className="bg-gradient-to-br from-[#6b7850] to-[#5c5b44] rounded-xl p-6 text-white shadow-lg">
            {/* Gift Icon and Sender */}
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-white/20 rounded-full p-3">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 6h-2.18c.11-.31.18-.65.18-1a2.996 2.996 0 0 0-5.5-1.65l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 11 8.76l1-1.36 1 1.36L15.38 12 17 10.83 14.92 8H20v6z"/>
                </svg>
              </div>
              <div>
                <div className="text-sm opacity-80">Gift from</div>
                <div className="font-semibold">{attachment.gift_card_sender_name || CREATOR_NAME}</div>
              </div>
            </div>

            {/* Amount */}
            <div className="text-center mb-4">
              <div className="text-4xl font-bold">${(attachment.gift_card_amount || 0).toFixed(2)}</div>
              <div className="text-sm opacity-80">Gift Card</div>
            </div>

            {/* Claim Button */}
            {isClaimed ? (
              <button
                disabled
                className="w-full py-3 bg-white/30 rounded-lg font-medium cursor-not-allowed flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Claimed
              </button>
            ) : (
              <button
                onClick={() => claimGiftCard(giftCardCode)}
                disabled={isClaiming}
                className="w-full py-3 bg-white text-[#5c5b44] rounded-lg font-bold hover:bg-gray-100 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isClaiming ? (
                  <>
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Claiming...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20 6h-2.18c.11-.31.18-.65.18-1a2.996 2.996 0 0 0-5.5-1.65l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 11 8.76l1-1.36 1 1.36L15.38 12 17 10.83 14.92 8H20v6z"/>
                    </svg>
                    Claim Gift
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )
    }

    // GIF attachment (external KLIPY GIFs)
    // Check type, is_external flag, or KLIPY URL pattern for robustness
    const isGif = attachment.type === 'gif' ||
                  attachment.is_external ||
                  (attachment.url && attachment.url.includes('klipy.com')) ||
                  attachment.klipy_id
    if (isGif) {
      return (
        <div key={key} className="relative group max-w-[300px]">
          {/* Blur preview placeholder */}
          {loadingStates[key] !== false && attachment.blur_preview && (
            <img
              src={attachment.blur_preview}
              alt=""
              className="absolute inset-0 w-full h-full object-cover rounded-xl"
            />
          )}
          <img
            src={attachment.url}
            alt={attachment.filename || 'GIF'}
            className={`rounded-xl w-full cursor-pointer hover:opacity-90 transition-opacity ${
              loadingStates[key] !== false && attachment.blur_preview ? 'opacity-0' : 'opacity-100'
            }`}
            style={{ maxHeight: '300px' }}
            onClick={() => setLightboxImage(attachment.url)}
            onLoad={() => handleImageLoad(key)}
            onError={() => handleImageError(key)}
            loading="lazy"
          />
          {/* GIF badge */}
          <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs font-bold px-2 py-0.5 rounded">
            GIF
          </div>
        </div>
      )
    }

    // IMPORTANT: Check media_type FIRST before type
    // Products always have type='image' but media_type tells us the actual content type

    // Video attachment (check media_type first!)
    if (attachment.media_type === 'video' || attachment.type === 'video') {
      const isPaidProduct = attachment.is_paid === true
      const isPurchased = attachment.is_purchased === true
      const timeRemaining = timerStates[key]
      const hasExpiredTimer = attachment.timer?.has_timer && (timeRemaining === 0 || attachment.timer?.is_expired)

      // Expired video product
      if (hasExpiredTimer && !isPurchased) {
        return (
          <div key={key} className="bg-gray-50 rounded-xl p-6 text-center border-2 border-dashed border-gray-300">
            <div className="text-gray-400 mb-2">🎬</div>
            <div className="text-sm text-gray-600 font-medium">This video offer has expired</div>
          </div>
        )
      }

      // Locked video product - SAME UI AS IMAGES
      if (isPaidProduct && !isPurchased) {
        return (
          <div key={key} className="relative overflow-hidden rounded-xl max-w-sm">
            {/* Blurred preview */}
            <img
              src={attachment.preview_url || attachment.thumbnail_url}
              alt="Locked video"
              className="w-full"
              style={{ maxHeight: '400px', filter: 'blur(35px)' }}
            />

            {/* Simple overlay with unlock button - SAME AS IMAGES */}
            <div
              className="absolute inset-0 flex flex-col items-center justify-center"
              style={{
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)'
              }}
            >
              <button
                onClick={async (e) => {
                  e.stopPropagation()
                  // Track unlock attempt
                  if (attachment.product_id) {
                    await trackUnlockAttempt(attachment.product_id)
                  }
                  setPurchaseModalProduct({
                    id: attachment.product_id!,
                    title: attachment.title || 'Video',
                    price: attachment.price || 0,
                    previewUrl: attachment.preview_url || attachment.thumbnail_url
                  })
                }}
                className="px-6 py-3 rounded-lg font-medium bg-white text-gray-900 shadow-md transition-all duration-200 hover:bg-gray-50"
              >
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  <span>Unlock</span>
                </span>
              </button>

              {/* Timer countdown */}
              {attachment.timer?.has_timer && timeRemaining && timeRemaining > 0 && (
                <div className="mt-4 px-4 py-2 rounded-lg bg-white/90 backdrop-blur-sm">
                  <div className="text-sm font-semibold text-red-600 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Expires in {formatTimer(timeRemaining)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      }

      // Unlocked video
      return (
        <div key={key} className="relative max-w-lg">
          <video
            controls
            className="rounded-xl w-full shadow-md"
            style={{ maxHeight: '400px' }}
            poster={attachment.thumbnail_url}
          >
            <source src={attachment.url} type={attachment.mime_type || 'video/mp4'} />
            Your browser does not support the video tag.
          </video>
        </div>
      )
    }

    // Audio freebie handling (voice_note with is_freebie flag)
    if (attachment.media_type === 'voice_note' && attachment.is_freebie) {
      const isFreebieUnlocked = attachment.freebie_unlocked === true || (attachment.freebie_id && unlockedFreebies[attachment.freebie_id])
      const isUnlocking = attachment.freebie_id ? unlockingFreebies[attachment.freebie_id] : false

      // Locked audio freebie
      if (!isFreebieUnlocked) {
        return (
          <div key={key} className="w-full max-w-sm">
            <div className="rounded-2xl p-5 bg-gradient-to-br from-green-50 to-green-100 shadow-sm border border-green-200">
              {/* Header with locked icon */}
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-green-200 rounded-xl p-3 flex-shrink-0">
                  <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5z"/>
                    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-green-800 truncate">{formatAudioTitle(attachment)}</div>
                  {attachment.duration_seconds && (
                    <div className="text-xs text-green-600">{formatDuration(attachment.duration_seconds)}</div>
                  )}
                </div>
                <div className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full flex-shrink-0">
                  FREE
                </div>
              </div>

              {/* Static waveform visual */}
              <div className="bg-green-200/50 rounded-xl p-3 mb-4">
                <div className="flex items-center justify-center gap-[3px] h-12">
                  {[...Array(35)].map((_, i) => (
                    <div
                      key={i}
                      className="w-[3px] bg-green-400 rounded-full"
                      style={{
                        height: `${25 + Math.sin(i * 0.4) * 35 + (i % 3) * 12}%`,
                        filter: 'blur(1px)'
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Unlock button */}
              <button
                onClick={() => attachment.freebie_id && unlockFreebie(attachment.freebie_id)}
                disabled={isUnlocking}
                className="w-full py-3 rounded-xl font-medium bg-white text-green-700 shadow-sm transition-all duration-200 hover:bg-green-50 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isUnlocking ? (
                  <>
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Unlocking...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                    <span>Unlock Free</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )
      }

      // Unlocked audio freebie - simple thin format with light green background
      const freebieUrl = attachment.freebie_id && unlockedFreebies[attachment.freebie_id]
        ? unlockedFreebies[attachment.freebie_id].url
        : attachment.url

      return (
        <div key={key} className="max-w-xs">
          <div className="bg-green-50 rounded-lg p-3">
            {/* Header with icon and info */}
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5z"/>
                <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
              </svg>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-700 truncate">{formatAudioTitle(attachment)}</div>
              </div>
              <div className="rounded-full p-1 bg-green-500 flex-shrink-0">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            {/* Audio player */}
            <audio
              controls
              controlsList="nodownload"
              className="w-full h-8"
            >
              <source src={freebieUrl} type={attachment.mime_type} />
              Your browser does not support the audio element.
            </audio>
          </div>
        </div>
      )
    }

    // Image attachment (ONLY for actual images, not videos/audio!)
    if (attachment.type === 'image' && (!attachment.media_type || attachment.media_type === 'image')) {
      // Check if this is a paid product
      const isPaidProduct = attachment.is_paid === true
      const isPurchased = attachment.is_purchased === true

      // Check if timer expired
      const timeRemaining = timerStates[key]
      const hasExpiredTimer = attachment.timer?.has_timer && (timeRemaining === 0 || attachment.timer.is_expired)

      // If timer expired and not purchased, don't render
      if (hasExpiredTimer && !isPurchased) {
        return (
          <div key={key} className="bg-gray-50 rounded-xl p-6 text-center border-2 border-dashed border-gray-300">
            <div className="text-gray-400 mb-2">⏱️</div>
            <div className="text-sm text-gray-600 font-medium">This offer has expired</div>
          </div>
        )
      }

      // Freebie handling (free content, one-click unlock)
      const isFreebie = attachment.is_freebie === true
      const isFreebieUnlocked = attachment.freebie_unlocked === true || (attachment.freebie_id && unlockedFreebies[attachment.freebie_id])
      const isUnlocking = attachment.freebie_id ? unlockingFreebies[attachment.freebie_id] : false

      if (isFreebie && !isFreebieUnlocked) {
        return (
          <div key={key} className="relative overflow-hidden rounded-xl max-w-sm">
            {/* Blurred preview */}
            <img
              src={attachment.preview_url || attachment.url}
              alt="Locked Freebie"
              className="w-full"
              style={{ maxHeight: '400px', filter: 'blur(35px)' }}
            />

            {/* Overlay with unlock button - SAME AS PAID PRODUCTS */}
            <div
              className="absolute inset-0 flex flex-col items-center justify-center"
              style={{
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)'
              }}
            >
              <button
                onClick={() => attachment.freebie_id && unlockFreebie(attachment.freebie_id)}
                disabled={isUnlocking}
                className="px-6 py-3 rounded-lg font-medium bg-white text-gray-900 shadow-md transition-all duration-200 hover:bg-gray-50 disabled:opacity-50"
              >
                <span className="flex items-center gap-2">
                  {isUnlocking ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Unlocking...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                      </svg>
                      <span>Unlock</span>
                    </>
                  )}
                </span>
              </button>
            </div>
          </div>
        )
      }

      // Show unlocked freebie
      if (isFreebie && isFreebieUnlocked) {
        const freebieUrl = attachment.freebie_id && unlockedFreebies[attachment.freebie_id]
          ? unlockedFreebies[attachment.freebie_id].url
          : attachment.url

        return (
          <div key={key} className="relative group">
            <img
              src={freebieUrl}
              alt={attachment.filename}
              className="w-full max-w-sm rounded-xl cursor-pointer hover:opacity-95 transition-opacity"
              style={{ maxHeight: '400px' }}
              onClick={() => setLightboxImage(freebieUrl)}
            />
          </div>
        )
      }

      // Locked product (paid but not purchased)
      if (isPaidProduct && !isPurchased) {
        return (
          <div key={key} className="relative overflow-hidden rounded-xl max-w-sm">
            {/* Blurred preview */}
            <img
              src={attachment.preview_url || attachment.url}
              alt="Locked"
              className="w-full"
              style={{ maxHeight: '400px', filter: 'blur(35px)' }}
            />

            {/* Simple overlay with just blur */}
            <div
              className="absolute inset-0 flex flex-col items-center justify-center"
              style={{
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)'
              }}
            >
              <button
                onClick={async (e) => {
                  e.stopPropagation()
                  // Track unlock attempt
                  if (attachment.product_id) {
                    await trackUnlockAttempt(attachment.product_id)
                  }
                  setPurchaseModalProduct({
                    id: attachment.product_id!,
                    title: attachment.title || 'Product',
                    price: attachment.price || 0,
                    previewUrl: attachment.preview_url
                  })
                }}
                className="px-6 py-3 rounded-lg font-medium bg-white text-gray-900 shadow-md transition-all duration-200 hover:bg-gray-50"
              >
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  <span>Unlock</span>
                </span>
              </button>

              {/* Timer countdown */}
              {attachment.timer?.has_timer && timeRemaining && timeRemaining > 0 && (
                <div className="mt-4 px-4 py-2 rounded-lg bg-white/90 backdrop-blur-sm">
                  <div className="text-sm font-semibold text-red-600 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Expires in {formatTimer(timeRemaining)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      }

      // Unlocked product or regular image
      return (
        <div key={key} className="relative group">
          {loadingStates[key] !== false && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
              <svg className="animate-spin h-6 w-6 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          )}
          {errorStates[key] ? (
            <div className="bg-gray-100 rounded-lg p-4 flex flex-col items-center">
              <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-sm text-gray-500">Failed to load image</span>
            </div>
          ) : (
            <div className="relative">
              <img
                src={attachment.url}
                alt={attachment.filename}
                className="rounded-xl max-w-full cursor-pointer transition-all hover:opacity-90"
                style={{ maxHeight: '400px' }}
                onClick={(e) => {
                  e.stopPropagation() // Prevent message click handler from firing
                  setLightboxImage(attachment.url)
                }}
                onLoad={() => handleImageLoad(key)}
                onError={() => handleImageError(key)}
              />
              {/* Checkmark badge for recently unlocked products */}
              {attachment.product_id && recentlyUnlockedProducts?.has(attachment.product_id) && (
                <div className="absolute top-2 right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg animate-bounce">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </div>
          )}
        </div>
      )
    }

    // This duplicate video check is now handled above (removed to avoid unreachable code)

    // Audio attachment
    if (attachment.type === 'audio' || attachment.media_type === 'voice_note') {
      const isPaidProduct = attachment.is_paid === true
      const isPurchased = attachment.is_purchased === true
      const timeRemaining = timerStates[key]
      const hasExpiredTimer = attachment.timer?.has_timer && (timeRemaining === 0 || attachment.timer?.is_expired)

      // Expired audio product
      if (hasExpiredTimer && !isPurchased) {
        return (
          <div key={key} className="bg-gray-50 rounded-xl p-6 text-center border-2 border-dashed border-gray-300">
            <div className="text-gray-400 mb-2">🎵</div>
            <div className="text-sm text-gray-600 font-medium">This audio offer has expired</div>
          </div>
        )
      }

      // Locked audio product - night-sky themed styling with static waveform
      if (isPaidProduct && !isPurchased) {
        return (
          <div key={key} className="w-full max-w-sm">
            <div className="rounded-2xl p-5 bg-gradient-to-br from-[#f6f3ee] to-[#ece6dd] shadow-sm border border-[#ece6dd]">
              {/* Header with locked icon */}
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-[#ece6dd] rounded-xl p-3 flex-shrink-0">
                  <svg className="w-6 h-6 text-[#6b7850]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5z"/>
                    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#21231c] truncate">{formatAudioTitle(attachment)}</div>
                  {attachment.duration_seconds && (
                    <div className="text-xs text-[#5c5b44]">{formatDuration(attachment.duration_seconds)}</div>
                  )}
                </div>
                <div className="bg-[#6b7850] text-white text-xs font-bold px-2 py-1 rounded-full flex-shrink-0">
                  ${attachment.price?.toFixed(2)}
                </div>
              </div>

              {/* Static waveform visual (blurred/locked look) */}
              <div className="bg-[#ece6dd]/50 rounded-xl p-3 mb-4">
                <div className="flex items-center justify-center gap-[3px] h-12">
                  {[...Array(35)].map((_, i) => (
                    <div
                      key={i}
                      className="w-[3px] bg-[#bab6a8] rounded-full"
                      style={{
                        height: `${25 + Math.sin(i * 0.4) * 35 + (i % 3) * 12}%`,
                        filter: 'blur(1px)'
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Unlock button */}
              <button
                onClick={async (e) => {
                  e.stopPropagation()
                  if (attachment.product_id) {
                    await trackUnlockAttempt(attachment.product_id)
                  }
                  setPurchaseModalProduct({
                    id: attachment.product_id!,
                    title: attachment.title || 'Audio',
                    price: attachment.price || 0,
                    previewUrl: attachment.preview_url
                  })
                }}
                className="w-full py-3 rounded-xl font-medium bg-white text-[#5c5b44] shadow-sm transition-all duration-200 hover:bg-[#f6f3ee] flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <span>Unlock for ${attachment.price?.toFixed(2)}</span>
              </button>

              {/* Timer */}
              {attachment.timer?.has_timer && timeRemaining && timeRemaining > 0 && (
                <div className="mt-3 px-4 py-2 rounded-xl bg-red-50 border border-red-100">
                  <div className="text-sm font-semibold text-red-600 flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Expires in {formatTimer(timeRemaining)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      }

      // Unlocked audio - simple thin format with light background
      return (
        <div key={key} className="max-w-xs">
          <div className="bg-[#f6f3ee] rounded-lg p-3">
            {/* Header with icon and info */}
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-5 h-5 text-[#6b7850] flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5z"/>
                <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
              </svg>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-700 truncate">{formatAudioTitle(attachment)}</div>
              </div>
              {isPurchased && (
                <div className="rounded-full p-1 bg-green-500 flex-shrink-0">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>

            {/* Audio player */}
            <audio
              key={`audio-${key}-${audioRetry[key] || 0}`}
              controls
              controlsList="nodownload"
              className="w-full h-8"
              onError={() => handleAudioError(key)}
            >
              <source src={attachment.url} type={attachment.mime_type} />
              Your browser does not support the audio element.
            </audio>
          </div>
        </div>
      )
    }

    // File attachment (generic)
    return (
      <div key={key} className="bg-gray-100 rounded-lg p-3">
        <div className="flex items-center gap-3">
          <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-700">{attachment.filename}</div>
            <div className="text-xs text-gray-500">{formatFileSize(attachment.size)}</div>
          </div>
          <a
            href={attachment.url}
            download={attachment.filename}
            className="p-2 bg-white rounded-lg hover:bg-gray-50 transition-colors"
          >
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
          </a>
        </div>
      </div>
    )
  }

  if (!attachments || attachments.length === 0) return null

  return (
    <>
      <div className={`mt-2 space-y-2 ${isSentByMe ? 'items-end' : 'items-start'} flex flex-col`}>
        {attachments.map(renderAttachment)}
      </div>

      {/* Lightbox for images */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300"
            onClick={() => setLightboxImage(null)}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img
            src={lightboxImage}
            alt="Full size"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Purchase Modal */}
      {purchaseModalProduct && (
        <PurchaseProductModal
          product={purchaseModalProduct}
          onClose={() => setPurchaseModalProduct(null)}
          onSuccess={() => {
            // Close modal - toast notification will show success
            setPurchaseModalProduct(null)
            // Trigger a small delay then reload to fetch updated messages
            // This gives the webhook time to process and update the database
            setTimeout(() => {
              window.location.reload()
            }, 1000)
          }}
        />
      )}
    </>
  )
}